import { token } from '@/infrastructure/container';
import {
  type BomEntry,
  type CanonicalBundle,
  type InventorySnapshot,
  type ManufacturingOrderRow,
  type Part,
  type PurchaseOrderRow,
  type QuarantineRow,
  type SalesOrderRow,
  type SkuId,
  type SourceKind,
} from '@/domain/beforered';
import { type IDateParser, type DateFormat } from './DateParser';

export interface AssemblerSlotInput {
  source: SourceKind;
  rows: readonly Record<string, string>[];
  mapping: Record<string, string | null>;
}

export interface AssemblyResult {
  bundle: CanonicalBundle;
  quarantine: QuarantineRow[];
}

export interface IBundleAssembler {
  assemble(slots: readonly AssemblerSlotInput[], fallbackToday: number): AssemblyResult;
}

export const TOKEN_IBundleAssembler = token<IBundleAssembler>('IBundleAssembler');

const FIELD_TYPES: Record<SourceKind, Record<string, 'string' | 'number' | 'date'>> = {
  BOM: { parent: 'string', child: 'string', quantityPerParent: 'number', uom: 'string' },
  INVENTORY: { sku: 'string', onHand: 'number', asOf: 'date' },
  SALES: {
    orderNumber: 'string',
    customerName: 'string',
    sku: 'string',
    quantity: 'number',
    scheduledShipDate: 'date',
  },
  MFG: {
    workOrderNumber: 'string',
    sku: 'string',
    quantity: 'number',
    scheduledCompletionDate: 'date',
  },
  PURCHASE: {
    orderNumber: 'string',
    sku: 'string',
    vendorId: 'string',
    quantityOutstanding: 'number',
    expectedDeliveryDate: 'date',
  },
  PARTS: {
    id: 'string',
    description: 'string',
    uom: 'string',
    productionLeadTimeDays: 'number',
    purchaseLeadTimeDays: 'number',
    safetyStockUnits: 'number',
  },
};

const parseNumber = (s: string): number | null => {
  if (s.trim().length === 0) return null;
  const cleaned = s.replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

export class BundleAssembler implements IBundleAssembler {
  constructor(private dateParser: IDateParser) {}

  assemble(slots: readonly AssemblerSlotInput[], fallbackToday: number): AssemblyResult {
    const quarantine: QuarantineRow[] = [];
    const bom: BomEntry[] = [];
    const inventory: InventorySnapshot[] = [];
    const salesOrders: SalesOrderRow[] = [];
    const manufacturingOrders: ManufacturingOrderRow[] = [];
    const purchaseOrders: PurchaseOrderRow[] = [];
    const parts = new Map<SkuId, Part>();

    for (const slot of slots) {
      const types = FIELD_TYPES[slot.source];
      const dateFormats = this.detectDateFormats(slot, types);

      const get = (row: Record<string, string>, canonical: string): string => {
        const header = slot.mapping[canonical];
        if (!header) return '';
        return row[header] ?? '';
      };

      const getNum = (row: Record<string, string>, canonical: string): number | null =>
        parseNumber(get(row, canonical));

      const getDate = (row: Record<string, string>, canonical: string): number | null => {
        const fmt = dateFormats[canonical];
        if (!fmt) return null;
        return this.dateParser.parseWithFormat(get(row, canonical), fmt);
      };

      slot.rows.forEach((row, idx) => {
        try {
          switch (slot.source) {
            case 'BOM': {
              const parent = get(row, 'parent');
              const child = get(row, 'child');
              const q = getNum(row, 'quantityPerParent');
              if (!parent || !child || q === null || q <= 0) {
                quarantine.push({
                  source: 'BOM',
                  rowIndex: idx,
                  reason: 'missing parent/child/qty',
                  raw: row,
                });
                return;
              }
              bom.push({ parent, child, quantityPerParent: q, uom: get(row, 'uom') || 'EA' });
              break;
            }
            case 'INVENTORY': {
              const sku = get(row, 'sku');
              const onHand = getNum(row, 'onHand');
              if (!sku || onHand === null) {
                quarantine.push({
                  source: 'INVENTORY',
                  rowIndex: idx,
                  reason: 'missing sku/onHand',
                  raw: row,
                });
                return;
              }
              const asOf = slot.mapping['asOf']
                ? (getDate(row, 'asOf') ?? fallbackToday)
                : fallbackToday;
              inventory.push({ sku, onHand, asOf });
              break;
            }
            case 'SALES': {
              const sku = get(row, 'sku');
              const qty = getNum(row, 'quantity');
              const date = getDate(row, 'scheduledShipDate');
              if (!sku || qty === null || qty <= 0 || date === null) {
                quarantine.push({
                  source: 'SALES',
                  rowIndex: idx,
                  reason: 'missing sku/qty/date',
                  raw: row,
                });
                return;
              }
              salesOrders.push({
                orderNumber: get(row, 'orderNumber') || `SO-${idx + 1}`,
                customerName: get(row, 'customerName'),
                sku,
                quantity: qty,
                scheduledShipDate: date,
              });
              break;
            }
            case 'MFG': {
              const sku = get(row, 'sku');
              const qty = getNum(row, 'quantity');
              const date = getDate(row, 'scheduledCompletionDate');
              if (!sku || qty === null || qty <= 0 || date === null) {
                quarantine.push({
                  source: 'MFG',
                  rowIndex: idx,
                  reason: 'missing sku/qty/date',
                  raw: row,
                });
                return;
              }
              manufacturingOrders.push({
                workOrderNumber: get(row, 'workOrderNumber') || `WO-${idx + 1}`,
                sku,
                quantity: qty,
                scheduledCompletionDate: date,
              });
              break;
            }
            case 'PURCHASE': {
              const sku = get(row, 'sku');
              const qty = getNum(row, 'quantityOutstanding');
              const date = getDate(row, 'expectedDeliveryDate');
              if (!sku || qty === null || qty <= 0 || date === null) {
                quarantine.push({
                  source: 'PURCHASE',
                  rowIndex: idx,
                  reason: 'missing sku/qty/date',
                  raw: row,
                });
                return;
              }
              purchaseOrders.push({
                orderNumber: get(row, 'orderNumber') || `PO-${idx + 1}`,
                sku,
                vendorId: get(row, 'vendorId') || null,
                quantityOutstanding: qty,
                expectedDeliveryDate: date,
              });
              break;
            }
            case 'PARTS': {
              const id = get(row, 'id');
              if (!id) {
                quarantine.push({ source: 'PARTS', rowIndex: idx, reason: 'missing id', raw: row });
                return;
              }
              parts.set(id, {
                id,
                description: get(row, 'description') || id,
                uom: get(row, 'uom') || 'EA',
                productionLeadTimeDays: getNum(row, 'productionLeadTimeDays'),
                purchaseLeadTimeDays: getNum(row, 'purchaseLeadTimeDays'),
                safetyStockUnits: getNum(row, 'safetyStockUnits'),
              });
              break;
            }
          }
        } catch (cause) {
          quarantine.push({
            source: slot.source,
            rowIndex: idx,
            reason: cause instanceof Error ? cause.message : 'parse error',
            raw: row,
          });
        }
      });
    }

    // Hard referential integrity: every SKU on a demand, supply, or inventory row must
    // appear in either the BOM (parent or child) or the Parts master. A row referencing
    // an unknown SKU is a typo or a missing-data signal; surfacing it as a phantom leaf
    // recommendation would hide the bug from the planner.
    const known = new Set<SkuId>();
    for (const e of bom) {
      known.add(e.parent);
      known.add(e.child);
    }
    for (const id of parts.keys()) known.add(id);

    const filter = <T extends { sku: SkuId }>(rows: T[], source: SourceKind): T[] => {
      const out: T[] = [];
      rows.forEach((r, idx) => {
        if (known.has(r.sku)) {
          out.push(r);
          return;
        }
        quarantine.push({
          source,
          rowIndex: idx,
          reason: `SKU ${r.sku} not in BOM or Parts master`,
          raw: r as unknown as Record<string, unknown>,
        });
      });
      return out;
    };

    const bundle: CanonicalBundle = {
      parts,
      bom,
      inventory: filter(inventory, 'INVENTORY'),
      salesOrders: filter(salesOrders, 'SALES'),
      manufacturingOrders: filter(manufacturingOrders, 'MFG'),
      purchaseOrders: filter(purchaseOrders, 'PURCHASE'),
    };
    return { bundle, quarantine };
  }

  private detectDateFormats(
    slot: AssemblerSlotInput,
    types: Record<string, 'string' | 'number' | 'date'>,
  ): Record<string, DateFormat> {
    const out: Record<string, DateFormat> = {};
    for (const [canonical, type] of Object.entries(types)) {
      if (type !== 'date') continue;
      const header = slot.mapping[canonical];
      if (!header) continue;
      const samples: string[] = [];
      for (const r of slot.rows.slice(0, 50)) {
        const v = r[header];
        if (v) samples.push(v);
      }
      const detected = this.dateParser.detect(samples);
      out[canonical] = detected.format;
    }
    return out;
  }
}
